import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useAppLoadedState, useUserState } from '@/hooks/store';
import getParamAsString from '@/utils/getParamsAsString';
import redirect from '@/utils/legacy/redirect';
import { isExpired, shouldShowRenewal } from '@/utils/subscriptions';

const b2bUpsellRegex = /\/b2b\/[^/]+\/(expired|renew)/;

const useB2BRedirect = (): boolean => {
	const [isRedirecting, setIsRedirecting] = useState(false);
	const searchParams = useSearchParams();

	const appLoaded = useAppLoadedState();
	const user = useUserState();
	const subscription = user?.subscription;

	const checkB2BUser = () => {
		// If they don't have a B2B sub then we don't redirect anywhere
		if (!subscription || subscription.type !== 'b2b' || !subscription.b2b_details?.partner_id) {
			return;
		}

		// If they came from the /b2b/[partner]/expired or /b2b/[partner]/renew page, they want to be here, so don't redirect them away
		const refererParam = getParamAsString(searchParams?.get('referer'));
		const hasChosenB2CSubscription = refererParam && b2bUpsellRegex.test(refererParam);
		if (hasChosenB2CSubscription) {
			return;
		}

		// If they have an already-expired B2B subscription, take them to the expired page
		// (It will link them back here with the appropriate referer param if they really wanted to be here)
		if (isExpired(subscription)) {
			setIsRedirecting(true);
			redirect(`/b2b/${subscription.b2b_details.partner_slug}/expired`);
			return;
		}

		// If their subscription is still valid, there are also some cases where we take them to the renew screen
		// (e.g., if their sub expires in less than 10 days)
		if (shouldShowRenewal(subscription)) {
			setIsRedirecting(true);
			redirect(`/b2b/${subscription.b2b_details.partner_slug}/renew`);
		}
	};

	useEffect(
		() => {
			if (appLoaded) {
				checkB2BUser();
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[appLoaded],
	);

	return isRedirecting;
};

export default useB2BRedirect;
